/**
 * The report period — one model behind the shared PeriodSelector (spec 41,
 * board DR1PeriodSelector). Every report page lets the practice pick the period;
 * the Dashboard keeps its own two, so it doesn't use this.
 *
 * UI on mock data: dates are computed from a fixed "today" so the examples in the
 * spec line up (2 Oct 2026). Numbers on most pages scale the 30-day mock
 * (§5); the two reports in §4 carry their own exact per-range tables.
 */

export type PeriodKey = '24h' | '7d' | '30d' | '60d' | 'all' | 'custom';

export interface PeriodValue {
  key: PeriodKey;
  /** ISO yyyy-mm-dd, custom only. */
  from?: string;
  /** ISO yyyy-mm-dd, custom only. */
  to?: string;
}

export type PeriodVariant = 'agent' | 'report';

/** The mock "now" — matches the spec's worked examples (2 Oct 2026, 10:42). */
export const PERIOD_TODAY = new Date(2026, 9, 2, 10, 42, 0);

export const PERIOD_PRESETS: Record<PeriodVariant, { key: PeriodKey; label: string }[]> = {
  agent: [
    { key: '24h', label: 'Last 24 hours' },
    { key: '7d', label: 'Last 7 days' },
    { key: '30d', label: 'Last 30 days' },
    { key: '60d', label: 'Last 60 days' },
    { key: 'custom', label: 'Custom range…' },
  ],
  report: [
    { key: '7d', label: 'Last 7 days' },
    { key: '30d', label: 'Last 30 days' },
    { key: '60d', label: 'Last 60 days' },
    { key: 'all', label: 'Since it started' },
    { key: 'custom', label: 'Custom range…' },
  ],
};

const PRESET_LABEL: Record<Exclude<PeriodKey, 'custom'>, string> = {
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '60d': 'Last 60 days',
  all: 'Since it started',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function parseISO(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** "3 Sep" or, with year, "1 Aug 2026". */
export function fmtDate(d: Date, withYear = false): string {
  const day = d.getDate();
  const mon = MONTHS[d.getMonth()] ?? '';
  return withYear ? `${String(day)} ${mon} ${String(d.getFullYear())}` : `${String(day)} ${mon}`;
}

function daysAgo(n: number, today: Date): Date {
  const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  d.setDate(d.getDate() - n);
  return d;
}

/** The [from, to] window a value covers, given when the thing started. */
export function periodRange(
  value: PeriodValue,
  startedISO: string,
  today: Date = PERIOD_TODAY,
): { from: Date; to: Date } {
  const to = today;
  const started = parseISO(startedISO) ?? daysAgo(30, today);
  switch (value.key) {
    case '24h':
      return { from: daysAgo(1, today), to };
    case '7d':
      return { from: daysAgo(7, today), to };
    case '30d':
      return { from: daysAgo(30, today), to };
    case '60d':
      return { from: daysAgo(60, today), to };
    case 'all':
      return { from: started, to };
    case 'custom': {
      const from = value.from ? parseISO(value.from) ?? daysAgo(30, today) : daysAgo(30, today);
      const t = value.to ? parseISO(value.to) ?? today : today;
      return { from, to: t };
    }
  }
}

/** Whole days the value spans (used for the <7-day rate rule and scaling). */
export function periodDays(value: PeriodValue, startedISO: string, today: Date = PERIOD_TODAY): number {
  const { from, to } = periodRange(value, startedISO, today);
  return Math.max(1, Math.round((to.getTime() - from.getTime()) / 86_400_000));
}

/**
 * The closed-control label: a bold name + the grey date range. Presets read
 * "Last 30 days" + "3 Sep – 2 Oct"; a custom range reads just its dates,
 * "1 Aug – 31 Aug 2026" (§2).
 */
export function periodLabel(
  value: PeriodValue,
  startedISO: string,
  today: Date = PERIOD_TODAY,
): { name: string; dates: string } {
  const { from, to } = periodRange(value, startedISO, today);
  if (value.key === 'custom') {
    const sameYear = from.getFullYear() === to.getFullYear();
    return { name: '', dates: `${fmtDate(from, !sameYear)} – ${fmtDate(to, true)}` };
  }
  return { name: PRESET_LABEL[value.key], dates: `${fmtDate(from)} – ${fmtDate(to)}` };
}

/** Rates show from 7 days up; under that, counts only and a dash for rates (§3.2). */
export function showRates(value: PeriodValue, startedISO = '2000-01-01', today: Date = PERIOD_TODAY): boolean {
  if (value.key === '24h') return false;
  return periodDays(value, startedISO, today) >= 7;
}

const SCALE: Record<Exclude<PeriodKey, 'custom'>, number> = {
  '24h': 1 / 30,
  '7d': 1 / 4,
  '30d': 1,
  '60d': 1.9,
  all: 3,
};

/**
 * Scale a 30-day mock count to the chosen period (§5): 24h ≈ ÷30, 7d ≈ ÷4,
 * 60d ≈ ×1.9, a custom range by its days ÷ 30. Rounded. Pages with exact
 * per-range tables (the two reports in §4) don't use this.
 */
export function scaleByPeriod(
  base30: number,
  value: PeriodValue,
  startedISO = '2000-01-01',
  today: Date = PERIOD_TODAY,
): number {
  const factor = value.key === 'custom' ? periodDays(value, startedISO, today) / 30 : SCALE[value.key];
  return Math.round(base30 * factor);
}

/** Parse `?range=`/`?from=&to=` into a value; anything missing/invalid → 30 days. */
export function periodFromParams(params: URLSearchParams): PeriodValue {
  const from = params.get('from');
  const to = params.get('to');
  if (from && to && parseISO(from) && parseISO(to)) return { key: 'custom', from, to };
  const range = params.get('range');
  if (range === '24h' || range === '7d' || range === '30d' || range === '60d' || range === 'all') {
    return { key: range };
  }
  return { key: '30d' };
}

/** The params that represent a value (so a page can round-trip it in the URL). */
export function periodToParams(value: PeriodValue): Record<string, string> {
  if (value.key === 'custom' && value.from && value.to) return { from: value.from, to: value.to };
  if (value.key === '30d') return {};
  return { range: value.key };
}
